import {BUSINESS_DOCUMENT_TYPE, BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";

// Document concerné par une transition. Les statuts de business et d'avoir partagent certaines valeurs
// ('CREATION', 'ABORTED'...) : la règle peut donc aussi cibler le type de document.
export const STATUS_DOCUMENT_KIND = {
    ...BUSINESS_DOCUMENT_TYPE, // QUOTE, INVOICE
    CREDIT_NOTE: 'CREDIT_NOTE',
    DEPOSIT: 'DEPOSIT',
};

const ANY = '*';

// Article + nom du document pour construire les messages ("cette facture", "ce devis", "cet avoir").
const DOCUMENT_LABEL = {
    [STATUS_DOCUMENT_KIND.QUOTE]: 'ce devis',
    [STATUS_DOCUMENT_KIND.INVOICE]: 'cette facture',
    [STATUS_DOCUMENT_KIND.CREDIT_NOTE]: 'cet avoir',
    [STATUS_DOCUMENT_KIND.DEPOSIT]: 'cet acompte',
};

// ------------------------------------------------------------
// Règles de transition : c'est ICI (et seulement ici) qu'on ajoute un cas.
//   from      : statut actuel (valeur, tableau de valeurs, ou ANY)
//   to        : statut visé   (valeur, tableau de valeurs, ou ANY)
//   documents : (optionnel) types de document concernés (STATUS_DOCUMENT_KIND)
//   resolve   : (context) => informations sur la transition. Clés reconnues :
//                 warning : message d'avertissement affiché avant validation
//                 error   : message bloquant (le bouton "Valider" est désactivé)
//               Toute autre clé est conservée (confirmation, comportement spécifique...).
// Toutes les règles qui correspondent sont fusionnées dans l'ordre de la liste : une règle plus
// spécifique placée après une règle générale peut compléter ou remplacer ses clés.
// context : {documentType: STATUS_DOCUMENT_KIND, business, creditNote}
// ------------------------------------------------------------
const STATUS_TRANSITION_RULES = [
    {
        from: ANY,
        to: BUSINESS_STATUS_ENUM.ABORTED.value, // même valeur "ABORTED" côté avoir
        resolve: ({documentType}) => ({
            warning: documentType === STATUS_DOCUMENT_KIND.INVOICE
                ? "Attention : l'annulation de cette facture est irréversible et entraînera la création d'un avoir négatif associé à cette facture."
                : `Attention : l'annulation de ${DOCUMENT_LABEL[documentType] || 'ce document'} est irréversible.`,
        }),
    },
    // Exemple d'ajout :
    // {from: BUSINESS_STATUS_ENUM.SENT.value, to: BUSINESS_STATUS_ENUM.ABORTED.value, documents: [STATUS_DOCUMENT_KIND.QUOTE],
    //  resolve: () => ({warning: "Ce devis a déjà été envoyé au client."})},
];

function matches(pattern, value) {
    return pattern === ANY || (Array.isArray(pattern) ? pattern.includes(value) : pattern === value);
}

// Informations sur la transition currentStatus -> targetStatus (objet fusionné des règles applicables),
// ou null si rien à signaler (pas de changement, ou aucune règle).
export function getStatusTransition(currentStatus, targetStatus, context = {}) {
    if (!targetStatus || currentStatus === targetStatus) return null;

    const matched = STATUS_TRANSITION_RULES.filter(rule =>
        matches(rule.from, currentStatus)
        && matches(rule.to, targetStatus)
        && (!rule.documents || rule.documents.includes(context.documentType)));

    if (matched.length === 0) return null;
    return Object.assign({}, ...matched.map(rule => rule.resolve(context)));
}

// Message à afficher au-dessus du bouton "Valider" : {type: 'error' | 'warning', message} ou null.
export function getStatusTransitionMessage(currentStatus, targetStatus, context = {}) {
    const transition = getStatusTransition(currentStatus, targetStatus, context);
    if (!transition) return null;
    if (transition.error) return {type: 'error', message: transition.error};
    if (transition.warning) return {type: 'warning', message: transition.warning};
    return null;
}

// Transition bloquée (erreur) : "Valider" désactivé.
export function isStatusTransitionBlocked(currentStatus, targetStatus, context = {}) {
    const transition = getStatusTransition(currentStatus, targetStatus, context);
    return !!(transition && transition.error);
}